import {AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {debounceTime, firstValueFrom, Observable, of, switchMap, tap} from 'rxjs';
import { NotFoundOrAuthorizedService } from 'src/app/core/services/not-found-or-authorized.service';
import { Channel } from 'stream-chat';
import {
  ChannelService,
  ChatClientService,
  StreamChatModule,
  StreamI18nService,
  StreamMessage,
  StreamTextareaModule,
  ThemeService
} from 'stream-chat-angular';
import {MatDialog} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {Router} from '@angular/router';
import {AsyncPipe, Location} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {AddChannelComponent} from './add-channel/add-channel.component';
import {DefaultLayoutAlignDirective, DefaultLayoutDirective} from 'ngx-flexible-layout';
import {MatIconButton} from '@angular/material/button';
import {ChatService} from '../../core/services/chat.service';
import {LoginService} from '../../core/services/login.service';
import {environment} from '../../../environments/environment';
import {TranslateModule} from '@ngx-translate/core';
import {MatInput} from '@angular/material/input';
import {MsgBannerService} from '../../shared/components/msg-banner/services/msg-banner.service';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';

@Component({
  selector: 'app-chat',
  imports: [
    StreamChatModule,
    StreamTextareaModule,
    DefaultLayoutDirective,
    DefaultLayoutAlignDirective,
    AsyncPipe,
    MatIcon,
    ReactiveFormsModule,
    MatIconButton,
    TranslateModule,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnInit, AfterViewInit, OnDestroy {
  searchControl = new UntypedFormControl('');
  options: Channel[] = [];

  isMenuOpen = true;
  themeVersion: '1' | '2';
  theme$: Observable<string>;
  hideJumpToLatestButtonDuringScroll: boolean;

  checkActiveChannel = false;
  intervalDisconnect = 2 * 60 * 60000;

  isDisconnectInProgress = false;
  connectPromise?: Promise<any>;
  disconnectPromise?: Promise<any>;
  disconnectTimeoutIdTab: string | number | NodeJS.Timeout;
  isChatReady = false;
  private isDestroyed = false;

  @HostListener('window:popstate')
  onPopState() {
    this.channelService.reset();
    this.notFoundOrAuthorizedService.hide();
  }

  @HostListener('window:beforeunload')
  async onBeforeUnload() {
    if (this.getStreamSvc.chatClient) {
      await this.disconnect();
      console.log('page closed');
    }
  }

  @HostListener('document:visibilitychange')
  async visibilitychange() {
    await this.checkHiddenDocument();
  }

  constructor(
    public getStreamSvc: ChatClientService,
    public channelService: ChannelService,
    private streamI18nService: StreamI18nService,
    private notFoundOrAuthorizedService: NotFoundOrAuthorizedService,
    private location: Location,
    private dialog: MatDialog,
    private chatSvc: ChatService,
    private loginSvc: LoginService,
    private router: Router,
    private msgBanner: MsgBannerService,
    private themeService: ThemeService
  ) {
    this.streamI18nService.setTranslation();
    this.notFoundOrAuthorizedService.show();

    this.theme$ = themeService.theme$;

    this.setJumpToLatestOption();
  }

  ngOnInit() {
    this.notFoundOrAuthorizedService.show();
    this.channelService.reset();
    void this.initializeChat();

    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        tap(),
        switchMap((value) => {
          if (!this.isChatReady) {
            return of(null);
          }
          if (value?.length === 0) {
            this.channelService.reset();
            return this.initChannels();
          }
          if (value?.length < 3) {
            return of(null);
          }
          if (value instanceof Object || value === '' || !value) {
            return of(null);
          }

          const filter = {
            type: 'messaging',
            'member.user.name': {
              $autocomplete: value
            },
            members: { $in: [ this.loginSvc._eid ] }
          };

          this.channelService.reset();
          return this.channelService.init(filter);
        })
      ).subscribe({
        next: (channels: any) => (this.options = channels as Channel[])
      });
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.isChatReady = false;
    void this.disconnect();
  }

  setJumpToLatestOption() {
    if (
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPhone/i)
    ) {
      this.hideJumpToLatestButtonDuringScroll = true;
    } else {
      this.hideJumpToLatestButtonDuringScroll = false;
    }
  }

  async initChannels() {
    if (!this.isChatReady) {
      return;
    }

    await this.channelService.init({
      type: 'messaging',
      members: { $in: [this.loginSvc._eid] },
      joined: true
    }, {
      has_unread: -1, last_message_at: -1
    }, {
      limit: 200
    }).then(_response => {
        this.loadOldChannels();
        setTimeout(() => {
          this.autoOpenConversations();
        }, 1);
      }
    );
  }

  ngAfterViewInit(): void {
    this.notFoundOrAuthorizedService.show();
  }

  goBack() {
    this.location.back();
    this.channelService.reset();
  }

  redirectToMainPage() {
    this.notFoundOrAuthorizedService.hide();
    this.router.navigate(['/my-events']);
  }

  getActiveChannelFromHistoryState() {
    const filter = {
      type: 'messaging',
      cid: history.state.data,
      members: { $in: [ this.loginSvc._eid ] }
    };
    this.getStreamSvc.chatClient.queryChannels(filter, {}, {
      watch: true, // this is the default
      state: true,
      limit: 200
    }).then(res => {
      this.channelService.setAsActiveChannel(res[0]);
    });
  }

  loadChannelsUntilSelectedFound(channelCid: string) {
    let checkActiveChannel = false;
    this.channelService.channels$.subscribe(res => {
      res.forEach(channel => {
        if (channel.cid === channelCid) {
          checkActiveChannel = true;
        }
      });
      if (checkActiveChannel === false) {
        this.channelService.loadMoreChannels();
      }
    });
  }

  createChannel() {
    if (!this.isChatReady) {
      return;
    }

    this.dialog.open(AddChannelComponent, {
      disableClose: true
    }).afterClosed().subscribe(r => {
      if (r) {
        this.isMenuOpen = false;
      }
    });
  }

  async getToken(): Promise<string> {
    try {
      const response = await firstValueFrom(this.chatSvc.authenticate());
      return response.token;
    } catch {
      this.msgBanner.addMsgError('An error has occurred.Please try to refresh or contact your Administrator.');
    }
  }

  async checkHiddenDocument() {
    if (document.hidden) {
      console.log('tab left');
      this.disconnectTimeoutIdTab = setTimeout(async () => {
        this.redirectToMainPage();
      }, this.intervalDisconnect);
    } else {
      console.log('tab active');
      clearTimeout(this.disconnectTimeoutIdTab);
      this.disconnectTimeoutIdTab = undefined;
    }
  }

  private async disconnect() {
    if (this.isDisconnectInProgress) {
      return this.disconnectPromise;
    }

    this.isDisconnectInProgress = true;
    this.disconnectPromise = new Promise<void>(async (resolve, reject) => {
      if (!this.getStreamSvc.chatClient.user) {
        this.isDisconnectInProgress = false;
        resolve();
        return;
      }
      try {
        await this.getStreamSvc.disconnectUser();
        this.channelService.reset();
        this.isDisconnectInProgress = false;
        resolve();
      } catch (e) {
        this.isDisconnectInProgress = false;
        reject(e);
      }
    });

    return this.disconnectPromise;
  }

  private async initializeChat() {
    if (!this.loginSvc._eid) {
      return;
    }

    if (this.isDisconnectInProgress) {
      await this.disconnectPromise;
    }

    try {
      this.connectPromise = this.getStreamSvc.init(
        environment.chat.apiKey,
        this.loginSvc._eid,
        () => this.getToken()
      );

      await this.connectPromise;

      if (this.isDestroyed) {
        return;
      }

      if (!this.getStreamSvc.chatClient?.user?.id) {
        this.msgBanner.addMsgError('An error has occurred.Please try to refresh or contact your Administrator.');
      }

      if (this.getStreamSvc.chatClient?.wsConnection) {
        this.getStreamSvc.chatClient.wsConnection._log = console.log;
      }

      this.isChatReady = true;
      await this.initChannels();
    } catch (error) {
      console.error(error);
      this.msgBanner.addMsgError('An error has occurred.Please try to refresh or contact your Administrator.');
      this.isChatReady = false;
    }
  }

  private autoOpenConversations() {
    const chat_icon = document.getElementsByClassName('str-chat__header-hamburger');
    if (chat_icon[0] && window.innerWidth < 961) {
      (chat_icon[0] as HTMLElement).click();
    }
  }

  private loadOldChannels() {
    if (history.state.data) {
      this.getActiveChannelFromHistoryState();
      this.loadChannelsUntilSelectedFound(history.state.data);
    }
  }

  jumpToMessage(message: StreamMessage) {
    this.channelService.jumpToMessage(message.id, message.parent_id);
  }

  unpinMessage(message: StreamMessage) {
    this.channelService.unpinMessage(message);
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  hideChannel() {
    const channel = this.channelService.activeChannel;

    channel.hide().then(async () => {
      console.log('Channel hidden: ' + channel.cid);
      this.channelService.reset();
      await this.initChannels();
    }).catch((error) => {
      console.error('Error hiding channel:', error);
    });
  }
}
